import { useEffect, useState } from "react";
import { Button } from "@/components/ui/Button";
import { Dialog, DialogField, DialogFooter, dialogInputStyle } from "@/components/ui/Dialog";
import { useUpdateWorkspace } from "@/hooks/useUpdateWorkspace";
import { showToast } from "@/lib/toast";
import { t } from "@/lib/strings";
import type { UUID } from "@/lib/types";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: UUID;
  workspaceName: string;
  configured: boolean;
}

export function TeamsWebhookDialog({
  open,
  onOpenChange,
  workspaceId,
  workspaceName,
  configured,
}: Props) {
  const update = useUpdateWorkspace();
  // The URL is a secret — never returned by the API. The field starts blank;
  // saving blank while configured clears it, saving a URL sets it.
  const [value, setValue] = useState("");

  useEffect(() => {
    if (open) setValue("");
  }, [open]);

  async function save(url: string | null) {
    try {
      await update.mutateAsync({ workspaceId, payload: { teams_webhook_url: url } });
      showToast(url ? t.teamsWebhookSaved : t.teamsWebhookCleared, "success");
      onOpenChange(false);
    } catch (err) {
      showToast(err instanceof Error ? err.message : t.unknownError, "error");
    }
  }

  const trimmed = value.trim();

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => !update.isPending && onOpenChange(o)}
      title={t.teamsWebhookTitle.replace("{name}", workspaceName)}
      description={t.teamsWebhookDescription}
      width={480}
    >
      <DialogField label={t.teamsWebhookLabel} htmlFor="teams-webhook" hint={t.teamsWebhookHint}>
        <input
          id="teams-webhook"
          name="teams-webhook"
          autoComplete="off"
          placeholder={configured ? t.teamsWebhookPlaceholderConfigured : t.teamsWebhookPlaceholder}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          style={dialogInputStyle}
        />
      </DialogField>

      <DialogFooter>
        {configured ? (
          <Button variant="ghost" size="md" disabled={update.isPending} onClick={() => save(null)}>
            {t.teamsWebhookClear}
          </Button>
        ) : null}
        <Button
          variant="primary"
          size="md"
          disabled={update.isPending || trimmed.length === 0}
          onClick={() => save(trimmed)}
        >
          {update.isPending ? t.loading : t.save}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
